import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    Users, UserCheck, TrendingUp, Box, Package, AlertTriangle,
    Euro, ShoppingCart, Send, FileText, Clock, RefreshCw,
} from 'lucide-react';
import { FadeIn, StaggerList, CountUp, showErrorAlert } from '../../../../components/ui/index.js';

/** Cada cuánto se refrescan los datos (ms). */
const POLL_MS = 10000;

/**
 * Construye las tarjetas de KPI a partir de la respuesta real del backend.
 * Devuelve una lista plana lista para renderizar con CountUp.
 */
const buildStatItems = (d) => [
    { label: 'Ingresos del mes',    value: d.revenue.month,          icon: <Euro         size={20} />, color: 'text-success',   prefix: '€', decimals: 2 },
    { label: 'Ingresos totales',    value: d.revenue.total,          icon: <TrendingUp   size={20} />, color: 'text-primary',   prefix: '€', decimals: 2 },
    { label: 'Pend. de pago',       value: d.orders.pendingPayment,  icon: <ShoppingCart size={20} />, color: 'text-error'   },
    { label: 'En preparación',      value: d.orders.inPreparation,   icon: <Package      size={20} />, color: 'text-warning' },
    { label: 'Pedidos enviados',    value: d.orders.shipped,         icon: <Send         size={20} />, color: 'text-info'    },
    { label: 'Presup. por revisar', value: d.budgets.toReview,       icon: <FileText     size={20} />, color: 'text-warning' },
    { label: 'Esperando respuesta', value: d.budgets.waitingResponse,icon: <Clock        size={20} />, color: 'text-info'    },
    { label: 'Clientes activos',    value: d.clients.active,         icon: <UserCheck    size={20} />, color: 'text-success' },
    { label: 'Leads abiertos',      value: d.clients.leads,          icon: <TrendingUp   size={20} />, color: 'text-info'    },
    { label: 'Clientes totales',    value: d.clients.total,          icon: <Users        size={20} />, color: 'text-primary' },
    { label: 'Modelos 3D',          value: d.catalog.models3D,       icon: <Box          size={20} />, color: 'text-secondary' },
    { label: 'Productos en stock',  value: d.catalog.productsInStock,icon: <Package      size={20} />, color: 'text-accent'  },
];

/** Mapa { label: value } para diffear entre snapshots. */
const flatValues = (d) => Object.fromEntries(buildStatItems(d).map((s) => [s.label, s.value]));

const ALERT_CLASS = {
    error:   'alert-error',
    warning: 'alert-warning',
    info:    'alert-info',
};

const FLASH = {
    boxShadow: [
        '0 0 0 0 rgba(99,102,241,0)',
        '0 0 0 3px rgba(99,102,241,0.55)',
        '0 0 0 0 rgba(99,102,241,0)',
    ],
};

const DashboardAdmin = () => {
    const navigate = useNavigate();
    const [data, setData]               = useState(null);
    const [loading, setLoading]         = useState(true);
    const [error, setError]             = useState(null);
    const [changedKeys, setChangedKeys] = useState(() => new Set());

    const dataRef      = useRef(null);   // último snapshot, para diffear sin depender del estado
    const flashTimer   = useRef(null);

    useEffect(() => {
        let cancelled = false;

        const applyData = (fresh) => {
            const prev = dataRef.current;
            if (prev) {
                const a = flatValues(prev);
                const b = flatValues(fresh);
                const changed = new Set(Object.keys(b).filter((k) => a[k] !== b[k]));
                if (changed.size) {
                    setChangedKeys(changed);
                    clearTimeout(flashTimer.current);
                    flashTimer.current = setTimeout(() => {
                        if (!cancelled) setChangedKeys(new Set());
                    }, 1200);
                }
            }
            dataRef.current = fresh;
            setData(fresh);
        };

        const fetchStats = (isFirst = false) => {
            axios.get('/dashboard/admin-stats/')
                .then(({ data: fresh }) => {
                    if (cancelled) return;
                    setError(null);
                    applyData(fresh);
                })
                .catch((err) => {
                    if (cancelled || dataRef.current) return; // en refrescos de fondo, conserva el último dato
                    const msg = err.response?.data?.detail || 'No se pudieron cargar las estadísticas del panel.';
                    setError(msg);
                    showErrorAlert({ text: msg });
                })
                .finally(() => {
                    if (isFirst && !cancelled) setLoading(false);
                });
        };

        fetchStats(true);

        const intervalId = setInterval(() => {
            if (document.visibilityState === 'visible') fetchStats();
        }, POLL_MS);

        const onVisibility = () => {
            if (document.visibilityState === 'visible') fetchStats();
        };
        document.addEventListener('visibilitychange', onVisibility);

        return () => {
            cancelled = true;
            clearInterval(intervalId);
            clearTimeout(flashTimer.current);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, []);

    const statItems = data ? buildStatItems(data) : [];

    if (error && !data) {
        return (
            <div className="min-h-full flex flex-col items-center justify-center gap-4 p-6 text-center">
                <AlertTriangle size={40} className="text-error" />
                <p className="text-base-content/70">{error}</p>
                <button
                    className="btn btn-sm btn-primary gap-2"
                    onClick={() => window.location.reload()}
                >
                    <RefreshCw size={16} /> Reintentar
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-full max-h-full flex flex-col gap-4 overflow-auto scrollbar_mod p-1">

            {/* ── Estadísticas ── */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {loading
                    ? Array.from({ length: 12 }).map((_, i) => (
                        <div key={i} className="glassCard rounded-xl p-4 h-28 animate-pulse">
                            <div className="h-4 bg-base-content/10 rounded mb-3 w-3/4" />
                            <div className="h-8 bg-base-content/10 rounded w-1/2" />
                        </div>
                    ))
                    : statItems.map((stat) => (
                        <FadeIn key={stat.label} distance={12}>
                            <motion.div
                                className="glassCard rounded-xl p-4 flex flex-col gap-2"
                                animate={changedKeys.has(stat.label) ? FLASH : {}}
                                transition={{ duration: 0.9, ease: 'easeOut' }}
                            >
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-base-content/60 font-medium leading-tight">{stat.label}</span>
                                    <span className={stat.color}>{stat.icon}</span>
                                </div>
                                <CountUp
                                    value={stat.value}
                                    decimals={stat.decimals || 0}
                                    prefix={stat.prefix || ''}
                                    className={`text-3xl font-black ${stat.color}`}
                                />
                            </motion.div>
                        </FadeIn>
                    ))
                }
            </div>

            {/* ── Bajo stock + Alertas ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 flex-1">

                {/* Bajo stock */}
                <div className="glassCard rounded-xl p-5 flex flex-col gap-3">
                    <h3 className="text-base font-bold text-base-content flex items-center gap-2">
                        <Package size={16} className="text-warning" />
                        Productos con bajo stock
                    </h3>
                    <div className="divider my-0" />
                    {loading ? (
                        <div className="flex flex-col gap-2">
                            {Array.from({ length: 5 }).map((_, i) => (
                                <div key={i} className="animate-pulse h-4 bg-base-content/10 rounded" />
                            ))}
                        </div>
                    ) : data.lowStockProducts.length === 0 ? (
                        <p className="text-sm text-base-content/50 py-2">Sin productos con bajo stock.</p>
                    ) : (
                        <StaggerList
                            items={data.lowStockProducts}
                            className="flex flex-col gap-1"
                            renderItem={(prod) => (
                                <div className="flex justify-between items-center py-1.5 border-b border-base-300 last:border-0">
                                    <span className="text-sm text-base-content">{prod.name}</span>
                                    <span className={`badge badge-sm font-mono ${prod.stock <= 2 ? 'badge-error' : 'badge-warning'}`}>
                                        {prod.stock} uds.
                                    </span>
                                </div>
                            )}
                        />
                    )}
                </div>

                {/* Alertas */}
                <div className="glassCard rounded-xl p-5 flex flex-col gap-3">
                    <h3 className="text-base font-bold text-base-content flex items-center gap-2">
                        <AlertTriangle size={16} className="text-error" />
                        Alertas
                    </h3>
                    <div className="divider my-0" />
                    {loading ? (
                        <div className="flex flex-col gap-2">
                            {Array.from({ length: 4 }).map((_, i) => (
                                <div key={i} className="animate-pulse h-4 bg-base-content/10 rounded" />
                            ))}
                        </div>
                    ) : data.alerts.length === 0 ? (
                        <p className="text-sm text-success py-2 font-medium">Todo en orden — sin alertas pendientes.</p>
                    ) : (
                        <StaggerList
                            items={data.alerts}
                            className="flex flex-col gap-2"
                            renderItem={(alert) => (
                                <div
                                    role="alert"
                                    onClick={alert.link ? () => navigate(alert.link) : undefined}
                                    className={`alert ${ALERT_CLASS[alert.level] || 'alert-warning'} py-2 text-sm ${alert.link ? 'cursor-pointer hover:brightness-95 transition' : ''}`}
                                >
                                    <AlertTriangle size={14} />
                                    <span>{alert.message}</span>
                                </div>
                            )}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default DashboardAdmin;
